import React from 'react';
import { useNavigate } from 'react-router-dom';
import { dateFormatting } from '../../utils/dateMethods';
import {
  Button,
  RoleManagementDelete,
  Modal,
  ToastBar,
  RoleManagementBlocked,
  AccessControl,
  PERMISSION_KEYS,
  PERMISSION_TYPES,
  hasAccess,
  Overlay,
} from '../../components';
import { RolesManagementService } from '../../services';
import { RoleManagementForm } from '../../forms';
import { exportToCSV, exportToExcel } from '../../utils/exportToExcel';
import { connect } from 'react-redux';
import { fetchRolesList } from '../../actions';
import blockIcon from '../../assets/svg/block.svg';
import unblock from '../../assets/svg/unblock.svg';
import editIcon from '../../assets/svg/edit.svg';
import deleteIcon from '../../assets/svg/delete.svg';
import '../LayoutWrapperMain/LayoutWrapperMain.global.scss';
import DataTable from '../Datatable';

interface Props {
  rolesList: any;
  fetchRoles: any;
}

const RolesManagement: React.FC<Props> = (props: Props) => {
  const {
    rolesList,
    fetchRoles,
  } = props;
  const navigate = useNavigate();
  const [filter, setFilter] = React.useState<any>({
    page: 0,
    rowsPerPage: 10,
    startDate: '',
    endDate: '',
    userType: '',
    search: '',
    isSortByRole: '',
    isSortByCreated: '',
  });
  const [error, setError] = React.useState<any>(null);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [showModal, setShowModal] = React.useState(false);
  const [loaderExport, setLoaderExport] = React.useState(false);
  const [showOverlay, setShowOverlay] = React.useState(false);
  const [blocked, setBlocked] = React.useState<any>({
    show: false,
    id: null,
  });
  const [deleted, setDeleted] = React.useState<any>({
    show: false,
    id: null,
  });
  const [unblocked, setUnBlocked] = React.useState<any>({
    show: false,
    id: null,
  });
  const [edited, setEdited] = React.useState<any>({
    show: false,
    id: null,
  });
  const [selectedRows, setSelectedRows] = React.useState<any>([]);

  const [notification, setNotification] = React.useState({
    show: false,
    message: '',
  });

  const hideActions = () => hasAccess(PERMISSION_KEYS.ROLE_MANAGEMENT, PERMISSION_TYPES.ADD)
    || hasAccess(PERMISSION_KEYS.ROLE_MANAGEMENT, PERMISSION_TYPES.EDIT)
    || hasAccess(PERMISSION_KEYS.ROLE_MANAGEMENT, PERMISSION_TYPES.DELETE);

  React.useEffect(() => {
    fetchRoles(filter);
  }, [fetchRoles, filter]);
  const handleSelectedClick = (event: React.MouseEvent<unknown>, name: any) => {
    const selectedIndex = selected.indexOf(name.id);
    let newSelected: string[] = [];
    let newSelectedRows: any = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name.id);
      newSelectedRows = newSelectedRows.concat(selectedRows, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
      newSelectedRows = newSelectedRows.concat(selectedRows.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
      newSelectedRows = newSelectedRows.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
      newSelectedRows = newSelectedRows.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1),
      );
    }
    setSelectedRows(newSelectedRows);
    setSelected(newSelected);
  };
  const handleBlockUnblock = (modalValue: any, blockedid: string) => {
    setShowModal(true);
    switch (modalValue) {
      case 'blocked':
        setBlocked({ ...blocked, show: true, id: blockedid });
        break;
      case 'unblocked':
        setUnBlocked({ ...unblocked, show: true, id: blockedid });
        break;
      case 'delete':
        setDeleted({ ...deleted, show: true, id: blockedid });
        break;
      case 'edit':
        navigate(`/rolemanagement/editRoles/${blockedid}`);
        break;
      default:
        break;
    }
  };
  const actionButtons = (action: any, idS: any) => (
    <div className="actionBtnsWrapper">
      <AccessControl
        permissionKey={PERMISSION_KEYS.ROLE_MANAGEMENT}
        accessType={PERMISSION_TYPES.EDIT}
      >
        <div>
          <img
            src={editIcon}
            role="presentation"
            alt="edit"
            width="35"
            title="Edit"
            height="35"
            onClick={() => handleBlockUnblock('edit', idS)}
          />
        </div>
      </AccessControl>
      <AccessControl
        permissionKey={PERMISSION_KEYS.ROLE_MANAGEMENT}
        accessType={PERMISSION_TYPES.EDIT}
      >
        <div>
          <img
            src={deleteIcon}
            role="presentation"
            alt="delete"
            width="35"
            title="Delete"
            height="35"
            onClick={() => handleBlockUnblock('delete', idS)}
          />
        </div>
      </AccessControl>
      <AccessControl
        permissionKey={PERMISSION_KEYS.ROLE_MANAGEMENT}
        accessType={PERMISSION_TYPES.EDIT}
      >
        <div>
          {action === 'draft' ? null : (action === 'unblocked' ? (
            <img
              src={blockIcon}
              role="presentation"
              alt="block"
              width="35"
              title="Block"
              height="35"
              onClick={() => handleBlockUnblock('blocked', idS)}
            />
          ) : (
            <img
              src={unblock}
              role="presentation"
              alt="unblock"
              width="35"
              title="Unblock"
              height="35"
              onClick={() => handleBlockUnblock('unblocked', idS)}
            />
          ))}
        </div>
      </AccessControl>
    </div>
  );
  const handleChangePage = (event: unknown, newPage: number) => {
    setFilter({ ...filter, page: newPage });
  };
  const count = rolesList.data ? rolesList.data['Pagination-Count'] : 0;
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({ ...filter, page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const handleSubmitRoleManagement = (data: any) => {
    if (data.endDate !== null
      || data.search !== ''
      || data.startDate !== null
      || data.userType !== '') {
      setFilter({
        ...filter,
        startDate: data.startDate ? data.startDate.toISOString() : '',
        endDate: data.endDate ? data.endDate.toISOString() : '',
        search: data.search.label,
        userType: data.userType,
        page: 0,
      });
    }
    setError(() => null);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setBlocked(false);
    setDeleted({ ...deleted, show: false, id: null });
    setBlocked({ ...blocked, show: false, id: null });
    setEdited({ ...edited, show: false, id: null });
    setUnBlocked({ ...unblocked, show: false, id: null });
  };

  function getStatus(status: string) {
    switch (status) {
      case 'unblocked':
        return 'Active';
      case 'draft':
        return 'Draft';
      default:
        return 'Blocked';
    }
  }

  function createData(data: any, index: number): any {
    const idS = data._id;
    return {
      sno: filter.page * filter.rowsPerPage + index + 1,
      id: idS,
      name: data.role,
      addedon: dateFormatting(data.createdAt),
      status: getStatus(data.status),
      ...(hideActions() && {
        action: actionButtons(data.status, idS),
      }),
    };
  }

  const rows: any = [];
  if (rolesList.data !== null && rolesList.data) {
    rolesList.data.data.forEach((ele: any, index: number) => {
      rows.push(createData(ele, index));
    });
  }

  const headCells: any = [
    {
      id: 'sno', label: 'S.No', sort: false,
    },
    {
      id: 'name', label: 'Role Name', sort: true,
    },
    {
      id: 'addedon', label: 'Added on', sort: true,
    },
    {
      id: 'status', label: 'Status', sort: false,
    },
    ...(hideActions() ? [{
      id: 'action', label: 'Action',
    }] : []),
  ];
  const handleClick = (event: any, name: any) => {
    if (event.target.localName === 'input') {
      handleSelectedClick(event, name);
    }
  };

  const handleResetSorts = () => {
    setFilter({
      ...filter,
      startDate: '',
      endDate: '',
      userType: '',
      search: '',
      isSortByRole: '',
      isSortByCreated: '',
    });
  };

  const handleSort = (order: any, orderBy: any) => {
    if (orderBy === 'name') {
      setFilter({
        ...filter,
        page: 0,
        isSortByRole: order === 'asc' ? '1' : '-1',
      });
    }
    if (orderBy === 'addedon') {
      setFilter({
        ...filter,
        page: 0,
        isSortByCreated: order === 'asc' ? '1' : '-1',
      });
    }
  };
  const handleExport = (exportType: string) => {
    setLoaderExport(true);
    if (exportType === 'XLSX') {
      if (selectedRows.length > 0) {
        const selectedRowDataModified: any = [];
        selectedRows.forEach((ele: any) => {
          selectedRowDataModified.push({
            Sno: ele.sno,
            RoleName: ele.name,
            Status: ele.status,
            CreatedAt: ele.addedon,
          });
        });
        exportToExcel(selectedRowDataModified, 'RoleManagementData');
        setLoaderExport(false);
        setNotification({
          ...notification,
          show: true,
          message: 'Excel successfully downloaded.',
        });
      } else {
        RolesManagementService
          .getRolesExport({ ...filter, type: 'xlsx' }).then(() => {
            setLoaderExport(false);
            setNotification({
              ...notification,
              show: true,
              message: 'Excel has been sent on mail.',
            });
          }).catch((err: any) => {
            setLoaderExport(false);
            setNotification({
              show: true,
              message: err.response.data.message,
            });
          });
      }
    } else if (exportType === 'CSV') {
      if (selectedRows.length > 0) {
        const selectedRowDataModified: any = [];
        selectedRows.forEach((ele: any) => {
          selectedRowDataModified.push({
            Sno: ele.sno,
            RoleName: ele.name,
            Status: ele.status,
            CreatedAt: ele.addedon,
          });
        });
        exportToCSV(selectedRowDataModified, 'RoleManagementData');
        setLoaderExport(false);
        setNotification({
          ...notification,
          show: true,
          message: 'CSV successfully downloaded.',
        });
      } else {
        RolesManagementService
          .getRolesExport({ ...filter, type: 'csv' }).then(() => {
            setLoaderExport(false);
            setNotification({
              ...notification,
              show: true,
              message: 'CSV has been sent on mail.',
            });
          }).catch((err: any) => {
            setLoaderExport(false);
            setNotification({
              show: true,
              message: err.response.data.message,
            });
          });
      }
    }
  };

  return (
    <>
      {showOverlay && <Overlay />}
      <Modal
        id="block-user"
        isOpen={showModal}
        onClose={handleModalClose}
        onManageDisableScrolling={() => null}
        lightCloseButton={false}
      >
        {
          deleted.show && (
            <RoleManagementDelete
              handleModalClose={handleModalClose}
              setShowOverlay={setShowOverlay}
              fetchDomesList={() => setFilter({ ...filter, page: 0 })}
              id={deleted.id}
            />
          )
        }
        {
          blocked.show && (
            <RoleManagementBlocked
              handleModalClose={handleModalClose}
              setShowOverlay={setShowOverlay}
              fetchDomesList={() => setFilter({ ...filter, page: 0 })}
              id={blocked.id}
              status="blocked"
            />
          )
        }
        {
          unblocked.show && (
            <RoleManagementBlocked
              handleModalClose={handleModalClose}
              setShowOverlay={setShowOverlay}
              fetchDomesList={() => setFilter({ ...filter, page: 0 })}
              id={unblocked.id}
              status="unblocked"
            />
          )
        }
      </Modal>
      {notification.show && (
        <ToastBar
          message={notification.message}
          handleCloseMethod={() => setNotification({
            ...notification,
            show: false,
            message: '',
          })}
        />
      )}
      <div>
        <AccessControl
          permissionKey={PERMISSION_KEYS.ROLE_MANAGEMENT}
          accessType={PERMISSION_TYPES.ADD}
        >
          <div className="topButton">
            <Button
              onClick={() => navigate('/rolemanagement/addRoles')}
            >
              Add New Role
            </Button>
          </div>
        </AccessControl>
        <RoleManagementForm
          handleResetSorts={handleResetSorts}
          handleFormSubmit={handleSubmitRoleManagement}
          error={error}
          handleExport={handleExport}
          loaderExport={loaderExport}
        />
        <div className="dataTableCover">
          <DataTable
            headCells_={headCells}
            rows_={rows}
            rowClick={handleClick}
            selected={selected}
            setSelected={setSelected}
            setSelectedRows={setSelectedRows}
            page={filter.page}
            rowsPerPage={filter.rowsPerPage}
            loading={rolesList.loadingInProgress}
            handleChangePage={handleChangePage}
            handleSort={handleSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            count={count}
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: any) => {
  const { rolesmanagement } = state;
  return {
    rolesList: rolesmanagement,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  fetchRoles: (payload: any) => dispatch(fetchRolesList(payload)),
});
export default connect(mapStateToProps, mapDispatchToProps)(RolesManagement);
